import {useCallback, useEffect, useRef} from 'react';
import Analytics from '../utils/Analytics';
import Environment from '../utils/Environment';
import {PAYWALL_EVENTS, PaywallSlug} from '../analytics/paywallEvents';

/** Percentage-of-page marks that get their own event. Ascending, de-duplicated per page view. */
const DEFAULT_BUCKETS = [25, 50, 75, 100] as const;

type PaywallScrollDepthOptions = {
  /** Identifies which paywall these events came from, e.g. "social-proof-scroll". */
  paywall: PaywallSlug;
  /**
   * Extra properties to attach to the events, read at fire-time. Pass current values on
   * every render — the hook snapshots the latest into a ref so the listener attaches once
   * without re-binding.
   */
  properties?: Record<string, unknown>;
  /** Override the depth marks. Values are percentages of the scrollable range. */
  buckets?: readonly number[];
};

/**
 * Scroll-depth instrumentation for paywalls that put content below the CTA.
 *
 * A paywall can only be credited for content under the fold if people actually get there,
 * so this answers "how far down did they go" rather than the binary "did they scroll"
 * that the killed /social-proof screen tracked. Fires `[Web Payments] Paywall Scrolled`:
 *
 *  - once per crossed `depthBucket` (25/50/75/100 by default), carrying the percentage of
 *    the scrollable range reached. Buckets are per page view and never re-fire, so a user
 *    scrolling up and back down produces one event per mark, not a stream.
 *  - once per named `section`, via the returned {@link observeSection} ref callback, when
 *    that element is at least half on screen. Depth buckets are not comparable between
 *    arms whose pages are different lengths; a section impression is.
 *
 * A page that cannot scroll (content shorter than the viewport) fires nothing at all,
 * which reads correctly as "nobody reached it" rather than as missing data.
 *
 * Pair with {@link usePaywallAnalytics} for the View → Trial Selection → Purchase funnel.
 */
export default function usePaywallScrollDepth({
  paywall,
  properties,
  buckets = DEFAULT_BUCKETS,
}: PaywallScrollDepthOptions) {
  // Snapshot the latest values each render so the scroll listener attaches once and still
  // reads current props at fire-time.
  const paywallRef = useRef(paywall);
  paywallRef.current = paywall;
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;
  const bucketsRef = useRef(buckets);
  bucketsRef.current = buckets;

  const firedBucketsRef = useRef<Set<number>>(new Set());
  const firedSectionsRef = useRef<Set<string>>(new Set());

  const track = useCallback((extra: Record<string, unknown>) => {
    Analytics.track(PAYWALL_EVENTS.scrolled, {
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      paywall: paywallRef.current,
      ...propertiesRef.current,
      ...extra,
    });
  }, []);

  useEffect(() => {
    const firedBuckets = firedBucketsRef.current;

    const handleScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      // Nothing below the fold to reach — stay silent rather than reporting a false 100%.
      if (scrollable <= 0) return;

      const percent = ((window.scrollY || doc.scrollTop) / scrollable) * 100;
      bucketsRef.current.forEach(bucket => {
        if (percent + 0.5 >= bucket && !firedBuckets.has(bucket)) {
          firedBuckets.add(bucket);
          track({depthBucket: bucket});
        }
      });
    };

    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => window.removeEventListener('scroll', handleScroll);
  }, [track]);

  /**
   * Ref callback that reports the first time `name` is at least half visible. Attach it to
   * the section you care about: `<div ref={observeSection('social-proof')}>`.
   */
  const observeSection = useCallback(
    (name: string) => (node: HTMLElement | null) => {
      if (!node || firedSectionsRef.current.has(name)) return;
      // Older Safari on the link-out cohort predates IntersectionObserver; the funnel
      // events still work, this diagnostic just goes quiet for those sessions.
      if (typeof IntersectionObserver === 'undefined') return;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting || firedSectionsRef.current.has(name)) return;
            firedSectionsRef.current.add(name);
            track({section: name});
            observer.disconnect();
          });
        },
        {threshold: 0.5},
      );
      observer.observe(node);
    },
    [track],
  );

  return {observeSection};
}
